import { useState } from 'react';
import type { DeviceInfo } from './types';
import { Big, bigInput } from './ui';

export function LockScreen({
  info,
  onUnlock,
}: {
  info: DeviceInfo;
  onUnlock: (userId: number, pin: string) => Promise<string | null>;
}) {
  const [who, setWho] = useState<DeviceInfo['mechanics'][number] | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const press = (d: string) => setPin((p) => (p.length < info.pinLength ? p + d : p));
  const submit = async () => {
    if (!who) return;
    setBusy(true);
    setError(await onUnlock(who.id, pin));
    setPin('');
    setBusy(false);
  };

  if (!who) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="mb-1 text-3xl font-black">Who's working?</h1>
        <p className="mb-6 text-slate-600">{info.device.name}</p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {info.mechanics.map((m) => (
            <button
              key={m.id}
              onClick={() => { setWho(m); setError(null); }}
              className="min-h-[96px] rounded-2xl bg-slate-800 p-4 text-2xl font-bold text-white active:bg-slate-900"
            >
              {m.name}
              {m.lockedUntil && <span className="block text-sm font-normal text-amber-300">Locked</span>}
              {!m.hasPin && <span className="block text-sm font-normal text-slate-300">No PIN yet</span>}
            </button>
          ))}
        </div>
        {info.mechanics.length === 0 && <p className="text-lg">No mechanics set up yet. Ask the Manager.</p>}
        <a className="mt-10 inline-block min-h-[56px] py-3 text-lg text-blue-800 underline" href="/login?office=1">
          Office staff: sign in with Google
        </a>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md p-6 text-center">
      <button className="mb-4 min-h-[56px] text-lg text-blue-800 underline" onClick={() => { setWho(null); setPin(''); }}>
        ← Not {who.name}?
      </button>
      <h1 className="text-3xl font-black">{who.name}</h1>
      <p className="mb-4 text-slate-600">Enter your PIN</p>
      <input readOnly value={'●'.repeat(pin.length)} className={`${bigInput} mb-3 text-center tracking-[0.5em]`} aria-label="PIN" />
      {error && <p className="mb-3 rounded-lg bg-red-100 p-3 text-lg text-red-900">{error}</p>}
      <div className="grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <Big key={d} onClick={() => press(d)} className="text-3xl">{d}</Big>
        ))}
        <Big onClick={() => setPin((p) => p.slice(0, -1))} tone="warn">⌫</Big>
        <Big onClick={() => press('0')} className="text-3xl">0</Big>
        <Big tone="go" onClick={submit} disabled={busy || pin.length !== info.pinLength}>OK</Big>
      </div>
    </div>
  );
}

export function ChangePin({ length, onDone }: { length: number; onDone: (current: string, next: string) => Promise<string | null> }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const field = (v: string, set: (s: string) => void, label: string) => (
    <label className="mb-3 block text-left text-lg font-semibold">
      {label}
      <input type="password" inputMode="numeric" maxLength={length} value={v} onChange={(e) => set(e.target.value.replace(/\D/g, ''))} className={bigInput} />
    </label>
  );
  return (
    <div className="mx-auto max-w-md p-6">
      <h1 className="mb-2 text-3xl font-black">Choose your own PIN</h1>
      <p className="mb-4 text-slate-600">{length} digits. Not all the same, not 123456.</p>
      {field(cur, setCur, 'PIN the Manager gave you')}
      {field(next, setNext, 'New PIN')}
      {field(again, setAgain, 'New PIN again')}
      {error && <p className="mb-3 rounded-lg bg-red-100 p-3 text-lg text-red-900">{error}</p>}
      <Big
        tone="go"
        className="w-full"
        disabled={cur.length !== length || next.length !== length || next !== again}
        onClick={async () => setError(await onDone(cur, next))}
      >
        Save PIN
      </Big>
    </div>
  );
}
